import React, { useState } from 'react';
import { Typography, Box } from '@mui/material';
import ActivityLayout from '../ActivityLayout';

function SortNumbers({ onBack, onCorrect, initialTimer = null, globalTimer = null, onGlobalTimeUp, score }) {
  const [numbers, setNumbers] = useState(() => generateNumbers());
  const [placedNumbers, setPlacedNumbers] = useState(Array(5).fill(null));
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [emoji, setEmoji] = useState(null);
  const [showNext, setShowNext] = useState(false);

  function generateNumbers() {
    const nums = Array.from({ length: 5 }, (_, i) => i + 1);
    return nums.sort(() => Math.random() - 0.5);
  }

  const handleNumberClick = (index) => {
    if (showNext || numbers[index] === null) return;

    const emptyIndex = placedNumbers.findIndex(num => num === null);
    if (emptyIndex !== -1) {
      const newPlacedNumbers = [...placedNumbers];
      newPlacedNumbers[emptyIndex] = numbers[index];
      setPlacedNumbers(newPlacedNumbers);

      const newNumbers = [...numbers];
      newNumbers[index] = null; // Disable the selected number
      setNumbers(newNumbers);

      // Check if numbers are in order
      const isOrdered = newPlacedNumbers.every((num, i) => i === 0 || num > newPlacedNumbers[i - 1]);
      if (isOrdered) {
        setEmoji('🎉');
        setShowNext(true);
        onCorrect();
      }
    }
  };

  const handlePlacedNumberClick = (index) => {
    if (showNext || placedNumbers[index] === null) return;

    const newNumbers = [...numbers];
    const numberToRestore = placedNumbers[index];
    const restoreIndex = numbers.findIndex(num => num === null);
    newNumbers[restoreIndex] = numberToRestore;
    setNumbers(newNumbers);

    const newPlacedNumbers = [...placedNumbers];
    newPlacedNumbers[index] = null;
    setPlacedNumbers(newPlacedNumbers);
  };

  const handleNext = () => {
    setNumbers(generateNumbers());
    setPlacedNumbers(Array(5).fill(null));
    setSelectedIndex(null);
    setEmoji(null);
    setShowNext(false);
  };

  return (
    <ActivityLayout 
      onBack={onBack}
      timeLimit={initialTimer}
      globalTimeLimit={globalTimer}
      onTimeUp={() => {
        setEmoji('⏰');
        setShowNext(true);
      }}
      onGlobalTimeUp={onGlobalTimeUp}
      isPaused={showNext}
      score={score}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          mt: 4,
          position: 'relative',
        }}
      >
        <Typography variant="h6">
          Mettez les nombres dans l'ordre croissant
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
            mt: 2,
          }}
        >
          {numbers.map((number, index) => (
            <Box
              key={index}
              onClick={() => handleNumberClick(index)}
              sx={{
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: showNext ? 'default' : 'pointer',
                border: selectedIndex === index ? '3px solid #1976d2' : '2px solid #ccc',
                borderRadius: '8px',
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: showNext ? 'white' : '#f5f5f5',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Typography variant="h3">{number}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
            mt: 2,
          }}
        >
          {placedNumbers.map((number, index) => (
            <Box
              key={index}
              onClick={() => handlePlacedNumberClick(index)}
              sx={{
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #ccc',
                borderRadius: '8px',
                backgroundColor: 'white',
                cursor: number !== null ? 'pointer' : 'default',
              }}
            >
              <Typography variant="h3">{number !== null ? number : ''}</Typography>
            </Box>
          ))}
        </Box>

        {emoji && (
          <Typography variant="h1" sx={{ mt: 2 }}>{emoji}</Typography>
        )}

        {showNext && (
          <Box
            onClick={handleNext}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '2rem',
            }}
          >
            {emoji} {emoji === '⏰' ? 'Time\'s up!' : 'Click to continue'}
          </Box>
        )}
      </Box>
    </ActivityLayout>
  );
}

export default SortNumbers;
